/**
 * ClientBadge
 * Badge nella meta row che mostra il Lead/Cliente collegato alla chat.
 * - Collegato -> nome (clic: apre la scheda; il drawer Messaggi si chiude al cambio pagina)
 * - Numero su una sola scheda -> suggerimento da confermare, piu' "Scegli un'altra scheda"
 * - Numero su piu' schede -> "N schede con questo numero": si sceglie dall'elenco
 * - Nessuna scheda -> pulsante "Collega Lead/Cliente"
 * Le schede proposte rispettano la visibilita' dei lead del ruolo (searchClientsForChat).
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Building2, UserPlus, X, ExternalLink, Link2, ChevronDown, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { CreateClientModal } from '@/components/clients/CreateClientModal';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { etichettaNumero } from '@/lib/services/whatsapp/phoneDigits';
import { candidateDetails, matchCountLabel } from '@/lib/services/whatsapp/chatNumberMatch';
import { useChatLeadPrefill } from './useChatLeadPrefill';
import { useChatNumberCandidates } from './useChatClientCandidates';
import { ClientLinkPicker } from './ClientLinkPicker';

interface ClientBadgeProps {
  chatJid: string;
  chatName: string;
  organizationId: string;
}

const AMBER = 'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30';
const NEUTRAL = 'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border-[rgba(55,53,47,0.09)] dark:border-white/10 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10';

export function ClientBadge({ chatJid, chatName, organizationId }: ClientBadgeProps) {
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState<Record<string, string> | null>(null);
  const prefillLead = useChatLeadPrefill();
  const { canCreateLead, canCreateClient } = usePermissions();
  const ref = useRef<HTMLDivElement>(null);

  const assignments = useWhatsAppAgentStore(s => s.assignments);
  const linkChatToClient = useWhatsAppAgentStore(s => s.linkChatToClient);
  const unlinkChatFromClient = useWhatsAppAgentStore(s => s.unlinkChatFromClient);
  const wahaConfig = useWhatsAppChatStore(s => s.wahaConfig);

  // Cerca assignment esistente per questa chat
  const assignment = useMemo(
    () => assignments.find(a => a.chat_jid === chatJid),
    [assignments, chatJid]
  );

  const linkedClient = assignment?.client || null;

  // Schede con lo stesso numero (tutte), solo se la chat non e' collegata
  const { candidates, loading: candidatesLoading } = useChatNumberCandidates(chatJid, organizationId, !linkedClient);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    if (showPicker) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  const handleLink = useCallback(async (clientId: string) => {
    const configId = wahaConfig?.configId || '';
    await linkChatToClient(chatJid, clientId, organizationId, chatName, configId);
    setShowPicker(false);
  }, [chatJid, organizationId, chatName, wahaConfig?.configId, linkChatToClient]);

  const handleUnlink = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await unlinkChatFromClient(chatJid, organizationId);
  }, [chatJid, organizationId, unlinkChatFromClient]);

  // Telefono estratto dal JID per pre-compilare il form di creazione
  const phoneFromJid = useMemo(() => {
    if (chatJid.endsWith('@g.us')) return ''; // gruppi: nessun telefono
    const num = chatJid.split('@')[0].replace(/\D/g, '');
    return num.length >= 7 ? num : '';
  }, [chatJid]);

  // Nome e numero della chat, fonte WhatsApp e numero aziendale che ha ricevuto la chat.
  const handleOpenCreateModal = useCallback(async () => {
    setShowPicker(false);
    setCreateData(await prefillLead({
      channel: 'whatsapp',
      name: chatName,
      phone: phoneFromJid,
      receivedOn: etichettaNumero(wahaConfig?.phoneNumber),
    }));
    setShowCreateModal(true);
  }, [prefillLead, chatName, phoneFromJid, wahaConfig?.phoneNumber]);

  const handleClientCreated = useCallback(async (clientId: string) => {
    setShowCreateModal(false);
    await handleLink(clientId);
  }, [handleLink]);

  // ===== LINKED: mostra badge con nome cliente =====
  if (linkedClient) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${linkedClient.id}`);
          }}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
          {...tip(`Apri la scheda di ${linkedClient.business_name} (chiude i messaggi)`)}
        >
          <Building2 className="w-3 h-3" />
          <span className="truncate max-w-[150px]">{linkedClient.business_name}</span>
          <ExternalLink className="w-2.5 h-2.5 opacity-50" />
        </button>
        <button
          onClick={handleUnlink}
          className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-[#6F6E69] dark:text-white/60 hover:text-red-500 transition-colors"
          {...iconTip('Scollega dalla chat')}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  const single = !candidatesLoading && candidates.length === 1 ? candidates[0] : null;
  const many = !candidatesLoading && candidates.length > 1;

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      {single ? (
        <>
          <button
            onClick={() => void handleLink(single.id)}
            className={`${AMBER} animate-pulse-subtle`}
            {...tip(`Stesso numero di ${single.name} (${candidateDetails(single)}): clicca per collegarlo a questa chat`)}
          >
            <Link2 className="w-3 h-3" />
            <span className="truncate max-w-[150px]">{single.name}</span>
            <span className="text-[10px] opacity-80">da confermare</span>
          </button>
          <button
            onClick={() => setShowPicker(v => !v)}
            aria-expanded={showPicker}
            aria-label="Scegli un'altra scheda"
            className="p-1 rounded-md text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10"
            {...iconTip("Scegli un'altra scheda")}
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </>
      ) : many ? (
        <button
          onClick={() => setShowPicker(v => !v)}
          aria-expanded={showPicker}
          className={AMBER}
          {...tip('Il numero di questa chat è su più schede: scegli quale collegare')}
        >
          <Users className="w-3 h-3" />
          {matchCountLabel(candidates.length)}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      ) : (
        <button onClick={() => setShowPicker(v => !v)} aria-expanded={showPicker} className={NEUTRAL}>
          <UserPlus className="w-3 h-3" />
          Collega Lead/Cliente
        </button>
      )}

      {showPicker && (
        <ClientLinkPicker
          organizationId={organizationId}
          candidates={candidates}
          onPick={(id) => void handleLink(id)}
          onCreate={canCreateLead || canCreateClient ? () => void handleOpenCreateModal() : undefined}
        />
      )}

      {/* Modal creazione cliente */}
      <CreateClientModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        initialName={chatName}
        initialData={createData ?? undefined}
        isLead={canCreateLead}
        onClientCreated={handleClientCreated}
      />
    </div>
  );
}
